import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { KeyRound, ShieldCheck, AlertTriangle, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = new URLSearchParams(window.location.search).get("token") || "";

  const [state, setState] = useState("checking"); // checking | invalid | ready | done
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setState("invalid"); return; }
      try {
        const { data } = await api.get(`/auth/reset-password/validate`, { params: { token } });
        if (data.valid) { setInfo(data); setState("ready"); }
        else setState("invalid");
      } catch { setState("invalid"); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setError("");
    const strong = password.length >= 8 && /[A-Z]/.test(password) && /\d/.test(password) && /[^A-Za-z0-9]/.test(password);
    if (!strong) { setError("Password must be at least 8 characters and include an uppercase letter, a number, and a special character."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/auth/reset-password`, { token, password });
      localStorage.setItem("cp_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("cp_refresh", data.refresh_token);
      await refresh();
      setState("done");
      const dest = data.user?.role === "affiliate_manager" ? "/org-chart" : "/dashboard";
      setTimeout(() => navigate(dest, { replace: true }), 1200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="mesh-bg" />
      <div className="glass w-full max-w-md p-8 fade-up" style={{ borderRadius: 24 }} data-testid="reset-card">
        <div className="flex justify-center mb-5"><Logo size="md" /></div>

        {state === "checking" && (
          <div className="text-center py-6" data-testid="reset-checking">
            <div className="w-2 h-2 rounded-full bg-blue-500 pulse-soft mx-auto mb-3" />
            <p className="font-mono text-sm text-ink-2">Checking your reset link…</p>
          </div>
        )}

        {state === "invalid" && (
          <div className="text-center py-4" data-testid="reset-invalid">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(245,158,11,0.14)" }}>
              <AlertTriangle size={22} style={{ color: "#B45309" }} />
            </div>
            <h1 className="font-display text-xl font-semibold tracking-tight mb-1">This link isn't valid</h1>
            <p className="text-sm text-ink-2 mb-5">Password reset links expire after 1 hour and can only be used once. Request a fresh one from the login page.</p>
            <button onClick={() => navigate("/login")} className="btn-secondary w-full justify-center" data-testid="reset-to-login">Go to login</button>
          </div>
        )}

        {state === "ready" && (
          <>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: "linear-gradient(135deg, rgba(64,96,255,0.16), rgba(107,79,255,0.16))" }}>
              <KeyRound size={22} style={{ color: "#4060FF" }} />
            </div>
            <div className="kpi-label">Reset password</div>
            <h1 className="font-display text-2xl font-semibold tracking-tight mb-1">Choose a new password</h1>
            <p className="text-sm text-ink-2 mb-5">Setting a new password for <span className="font-mono text-xs">{info?.email}</span>. You'll be signed in automatically.</p>
            <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" className="input-glass w-full pr-11" data-testid="reset-password" autoFocus />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-1 transition-colors" data-testid="reset-password-toggle" aria-label={showPw ? "Hide password" : "Show password"}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <input type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" className="input-glass w-full" data-testid="reset-confirm" />
              <p className="text-[11px] text-ink-3">At least 8 characters, with one uppercase letter, one number, and one special character.</p>
              {error && <div className="chip chip-amber" data-testid="reset-error">{error}</div>}
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="reset-submit">
                {submitting ? "Saving…" : <>Set new password <ArrowRight size={15} /></>}
              </button>
            </form>
          </>
        )}

        {state === "done" && (
          <div className="text-center py-4" data-testid="reset-done">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(0,134,58,0.12)" }}>
              <ShieldCheck size={22} style={{ color: "#00863A" }} />
            </div>
            <h1 className="font-display text-xl font-semibold tracking-tight mb-1">Password updated!</h1>
            <p className="text-sm text-ink-2">Taking you to your dashboard…</p>
          </div>
        )}
      </div>
    </div>
  );
}
